import { ArdoqId, ViewIds } from '@ardoq/api-types';
import {
  DropdownItem,
  DropdownOption,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { pluralize } from '@ardoq/common-helpers';
import { type FeatureContext, featureOperations } from '@ardoq/features';
import { type PermissionContext } from '@ardoq/access-control';
import type { ContextShape, CurrentContext } from '@ardoq/data-model';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import { dispatchAction } from '@ardoq/rxbeach';
import { t, type I18nLocale } from '@ardoq/i18n';
import { IconName } from '@ardoq/icons';
import { getScenarioDisabledHelpText } from 'contextMenus/consts';
import { scenarioIsolationHelperText } from './ScenarioIsolationHelperText';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import { openAddToScenarioDialog } from './actions';
import {
  trackClickStartAddToScenario,
  trackClickStartCreatingNewScenario,
} from './tracking';

export const VIEWS_WITHOUT_SCENARIO_MENU = new Set<ViewIds>([
  ViewIds.BLOCKS,
  ViewIds.ASSISTANT_DIAGRAM,
  ViewIds.ASSISTANT_BLOCKS,
]);

export const VIEWS_WITH_COMPONENTS_IN_VIEW_LABEL = new Set<ViewIds>([
  ViewIds.DEPENDENCY_MAP_2,
  ViewIds.TIMELINE,
  ViewIds.BUBBLE,
  ViewIds.COMPONENT_MATRIX_2,
  /* Plots only components carrying a value for all three chosen fields, so "from components in
     view" is the accurate wording rather than "from view". */
  ViewIds.SURFACE_3D,
  /* `toXyzDataPoints` drops a component missing x, y or z on all four charts - that is what the
     exclusion notice reports - so the same wording is the accurate one here. */
  ViewIds.CONTOUR,
  ViewIds.SCATTER_3D,
  ViewIds.BUBBLE_3D,
  ViewIds.SPIDER_2,
  ViewIds.POLAR,
]);

type ViewScenarioSupport = {
  isViewpointMode: boolean;
  viewId: ViewIds;
};

const isScenarioMenuHidden = ({
  isViewpointMode,
  viewId,
}: ViewScenarioSupport): boolean =>
  isViewpointMode && VIEWS_WITHOUT_SCENARIO_MENU.has(viewId);

const withComponentCount = (label: string, componentCount: number): string =>
  componentCount === 0
    ? label
    : // TODO: pluralize is English-only; translate when i18n supports plural forms
      `${label} (${componentCount} ${pluralize('component', componentCount)})`;

const getCreateFromViewLabel = (
  { isViewpointMode, viewId }: ViewScenarioSupport,
  i18nLocale: I18nLocale
): string =>
  isViewpointMode && VIEWS_WITH_COMPONENTS_IN_VIEW_LABEL.has(viewId)
    ? t(
        i18nLocale,
        'appContainer:navigatorTopBar.createScenarioFromComponentsInView',
        'Create scenario from components in view'
      )
    : t(
        i18nLocale,
        'appContainer:navigatorTopBar.createScenarioFromView',
        'Create scenario from view'
      );

type ScenarioOptionArgs = ViewScenarioSupport & {
  createFromViewEnabled: boolean;
  createFromSelectionEnabled: boolean;
  disabledTooltipText: string | undefined;
  viewComponentIds: ArdoqId[];
  selectedComponentIds: ArdoqId[];
  loadedGraph: LoadedGraphWithViewpointMode;
  context: ContextShape;
  i18nLocale: I18nLocale;
};

const createScenarioFromViewOption = ({
  isViewpointMode,
  viewId,
  createFromViewEnabled,
  disabledTooltipText,
  viewComponentIds,
  loadedGraph,
  context,
  i18nLocale,
}: ScenarioOptionArgs): DropdownOption => ({
  label: withComponentCount(
    getCreateFromViewLabel({ isViewpointMode, viewId }, i18nLocale),
    createFromViewEnabled ? viewComponentIds.length : 0
  ),
  isDisabled: !createFromViewEnabled,
  tooltipText: disabledTooltipText,
  onClick: () => {
    dispatchAction(
      openAddToScenarioDialog({
        context,
        componentIds: viewComponentIds,
        loadedGraph,
      })
    );
    trackClickStartCreatingNewScenario(
      'topbarView',
      Boolean(loadedGraph?.isViewpointMode)
    );
  },
  type: DropdownOptionType.OPTION,
});

const createScenarioFromSelectionOption = ({
  createFromSelectionEnabled,
  disabledTooltipText,
  selectedComponentIds,
  loadedGraph,
  context,
  i18nLocale,
}: ScenarioOptionArgs): DropdownOption => ({
  label: withComponentCount(
    t(
      i18nLocale,
      'appContainer:navigatorTopBar.createScenarioFromSelection',
      'Create scenario from selection'
    ),
    createFromSelectionEnabled ? selectedComponentIds.length : 0
  ),
  isDisabled: !createFromSelectionEnabled,
  tooltipText: disabledTooltipText,
  onClick: () => {
    dispatchAction(
      openAddToScenarioDialog({
        context,
        componentIds: selectedComponentIds,
        loadedGraph,
      })
    );
    trackClickStartCreatingNewScenario(
      'topbarSelection',
      Boolean(loadedGraph?.isViewpointMode)
    );
  },
  type: DropdownOptionType.OPTION,
});

const addSelectionToScenarioOption = ({
  createFromSelectionEnabled,
  disabledTooltipText,
  selectedComponentIds,
  loadedGraph,
  context,
  i18nLocale,
}: ScenarioOptionArgs): DropdownOption => ({
  label: withComponentCount(
    t(
      i18nLocale,
      'appContainer:navigatorTopBar.addSelectionToScenario',
      'Add selection to scenario'
    ),
    createFromSelectionEnabled ? selectedComponentIds.length : 0
  ),
  isDisabled: !createFromSelectionEnabled,
  tooltipText: disabledTooltipText,
  onClick: () => {
    dispatchAction(
      openAddToScenarioDialog({
        context,
        componentIds: selectedComponentIds,
        addToExistingScenario: true,
        loadedGraph,
      })
    );
    trackClickStartAddToScenario('topbarSelection');
  },
  type: DropdownOptionType.OPTION,
});

export type ScenarioMenu = {
  menuButton: {
    tooltipText: string;
    ariaLabel: string;
    isDisabled: boolean;
  } | null;
  scenarioOptions: DropdownItem[];
};

const NO_SCENARIO_MENU: ScenarioMenu = {
  menuButton: null,
  scenarioOptions: [],
};

type ScenarioMenuArgs = ViewScenarioSupport & {
  activeScenarioId: ArdoqId | null;
  hasCollapsingRuleApplied: boolean;
  featureContext: FeatureContext;
  permissionContext: PermissionContext;
  viewComponentIds: ArdoqId[];
  selectedComponentIds: ArdoqId[];
  loadedGraph: LoadedGraphWithViewpointMode;
  context: ContextShape;
  i18nLocale: I18nLocale;
};

/**
 * The scenario button and its dropdown. A null `menuButton` means neither is
 * offered.
 */
export const getScenarioMenu = ({
  isViewpointMode,
  viewId,
  activeScenarioId,
  hasCollapsingRuleApplied,
  featureContext,
  permissionContext,
  viewComponentIds,
  selectedComponentIds,
  loadedGraph,
  context,
  i18nLocale,
}: ScenarioMenuArgs): ScenarioMenu => {
  if (
    viewId === ViewIds.NONE ||
    isScenarioMenuHidden({ isViewpointMode, viewId }) ||
    !featureOperations.hasScenarioFeature(featureContext) ||
    !permissionContext.user?.writeAccess
  ) {
    return NO_SCENARIO_MENU;
  }

  const createFromViewEnabled =
    !hasCollapsingRuleApplied &&
    !activeScenarioId &&
    viewComponentIds.length > 0;
  const createFromSelectionEnabled =
    !hasCollapsingRuleApplied &&
    !activeScenarioId &&
    selectedComponentIds.length > 0;
  const disabledTooltipText = hasCollapsingRuleApplied
    ? getScenarioDisabledHelpText(i18nLocale)
    : undefined;

  const optionArgs = {
    isViewpointMode,
    viewId,
    createFromViewEnabled,
    createFromSelectionEnabled,
    disabledTooltipText,
    viewComponentIds,
    selectedComponentIds,
    loadedGraph,
    context,
    i18nLocale,
  };

  const scenarioOptions: DropdownItem[] = [
    ...(scenarioAccessControlInterface.canCreateScenario(
      permissionContext,
      featureContext
    )
      ? [
          createScenarioFromViewOption(optionArgs),
          createScenarioFromSelectionOption(optionArgs),
        ]
      : []),
    addSelectionToScenarioOption(optionArgs),
    ...(isViewpointMode ? [scenarioIsolationHelperText(i18nLocale)] : []),
  ];

  return {
    menuButton: {
      tooltipText:
        disabledTooltipText ??
        t(i18nLocale, 'appContainer:navigatorTopBar.scenario', 'Scenario'),
      isDisabled: hasCollapsingRuleApplied,
      ariaLabel: t(
        i18nLocale,
        'appContainer:navigatorTopBar.scenarioOptions',
        'Scenario options'
      ),
    },
    scenarioOptions,
  };
};

/**
 * The scenario entries an entity context menu offers for the components it was
 * opened on. An empty array means the menu carries no scenario section.
 */
export type GetScenarioContextMenuOptions = (
  componentIds: ArdoqId[]
) => DropdownOption[];

type ScenarioContextMenuOptionsArgs = ViewScenarioSupport & {
  hasCollapsingRuleApplied: boolean;
  featureContext: FeatureContext;
  permissionContext: PermissionContext;
  context: CurrentContext;
  i18nLocale: I18nLocale;
};

const noScenarioContextMenuOptions: GetScenarioContextMenuOptions = () => [];

type ContextMenuOptionArgs = {
  componentIds: ArdoqId[];
  isDisabled: boolean;
  isViewpointMode: boolean;
  tooltipText: string | undefined;
  context: CurrentContext;
  i18nLocale: I18nLocale;
};

const addSelectionToScenarioContextMenuOption = ({
  componentIds,
  isDisabled,
  isViewpointMode,
  tooltipText,
  context,
  i18nLocale,
}: ContextMenuOptionArgs): DropdownOption => ({
  name: 'add-selection-to-scenario',
  testId: 'add-selection-to-scenario-item',
  label: t(
    i18nLocale,
    'contextMenus:addSelectionToScenario',
    'Add selection to scenario'
  ),
  iconName: IconName.SCENARIO,
  isDisabled,
  tooltipText,
  onClick: () => {
    dispatchAction(
      openAddToScenarioDialog({
        context,
        componentIds,
        addToExistingScenario: true,
      })
    );
    trackClickStartCreatingNewScenario('contextmenu', isViewpointMode);
  },
  type: DropdownOptionType.OPTION,
});

const createScenarioFromSelectionContextMenuOption = ({
  componentIds,
  isDisabled,
  isViewpointMode,
  tooltipText,
  context,
  i18nLocale,
}: ContextMenuOptionArgs): DropdownOption => ({
  name: 'create-scenario-from-selection',
  testId: 'create-scenario-from-selection-item',
  label: t(
    i18nLocale,
    'contextMenus:createScenarioFromSelection',
    'Create scenario from selection'
  ),
  iconName: IconName.SCENARIO,
  isDisabled,
  tooltipText,
  onClick: () => {
    dispatchAction(openAddToScenarioDialog({ context, componentIds }));
    trackClickStartCreatingNewScenario('contextmenu', isViewpointMode);
  },
  type: DropdownOptionType.OPTION,
});

/**
 * Resolves, for one view, which scenario entries its context menus may offer.
 * The returned closure binds them to the right-clicked components.
 */
export const getScenarioContextMenuOptionsForView = ({
  isViewpointMode,
  viewId,
  hasCollapsingRuleApplied,
  featureContext,
  permissionContext,
  context,
  i18nLocale,
}: ScenarioContextMenuOptionsArgs): GetScenarioContextMenuOptions => {
  if (
    isScenarioMenuHidden({ isViewpointMode, viewId }) ||
    !featureOperations.hasScenarioFeature(featureContext)
  ) {
    return noScenarioContextMenuOptions;
  }

  const tooltipText = hasCollapsingRuleApplied
    ? getScenarioDisabledHelpText(i18nLocale)
    : undefined;

  return componentIds => {
    const optionArgs = {
      componentIds,
      isDisabled: hasCollapsingRuleApplied,
      isViewpointMode,
      tooltipText,
      context,
      i18nLocale,
    };
    return [
      addSelectionToScenarioContextMenuOption(optionArgs),
      ...(scenarioAccessControlInterface.canCreateScenario(
        permissionContext,
        featureContext
      )
        ? [createScenarioFromSelectionContextMenuOption(optionArgs)]
        : []),
    ];
  };
};
